import {
  buildDtoolsHierarchySnapshotFromLines,
  buildDtoolsStagingLineRawText,
  stagingLineToConfirmItem,
} from "./dtoolsJsonConfirmImport";
import type { DtoolsStagingLine } from "./dtoolsJsonStaging";
import { buildReviewHierarchy, groupIdenticalProductsInReviewHierarchy } from "./dtoolsJsonStaging";
import { buildDtoolsProjectCatalogKey } from "./dtoolsProjectCatalogKey";
import {
  buildDtoolsStagingLinesFromProposalItems,
  hasDtoolsHierarchySnapshot,
  resolveDtoolsProposalItemsForLiveView,
  type ProposalDbItemForDtoolsView,
  type ProposalHeaderForDtoolsView,
  shouldRenderDtoolsBeautifiedProposalView,
} from "./dtoolsProposalLiveView";
import { DTOOLS_JSON_SOURCE } from "./dtoolsSiJsonImport";
import {
  applyHierarchyOverridesToLines,
  elevateServiceLinesToRoomPrimaries,
  sanitizeStagingLineFields,
  type DtoolsHierarchyOverrides,
} from "./dtoolsHierarchyOverrides";
import {
  formatProductBrandModelReference,
  resolveProductDisplayName,
} from "./productDisplayName";
import { isClarityStandardServiceProduct } from "./clarityServiceCatalogueItems";

export type DtoolsReviseLine = DtoolsStagingLine & {
  key: string;
  persistedItemId?: number;
};

export type DtoolsReviseCreateItemPayload = {
  description: string;
  unitPrice: number;
  qty: number;
  /** Authoritative line total (InstallPrice); falls back to unitPrice × qty only when absent. */
  lineTotal?: number;
  name?: string | null;
  brand?: string | null;
  model?: string | null;
  category?: string | null;
  rawText?: string | null;
  imageUrlSnapshot?: string | null;
  displayName?: string | null;
  displayNameManuallyEdited?: boolean;
  pricingMethod?: "fixed" | "percentage_of_equipment" | null;
  percentageOfEquipment?: number | null;
  cleoDescription?: string | null;
  customerExplanation?: string | null;
};

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

export function normalizeDtoolsGroupLabel(value: string | null | undefined): string {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

/** Matches buildReviewHierarchy: null/empty systemName → "Unassigned system". */
export function canonicalDtoolsLineSystemName(line: Pick<DtoolsReviseLine, "systemName">): string {
  const trimmed = String(line.systemName ?? "").trim();
  return trimmed || "Unassigned system";
}

export function findMatchingGroupLabel(
  candidates: string[],
  input: string,
): string | null {
  const norm = normalizeDtoolsGroupLabel(input);
  if (!norm) return null;
  for (const candidate of candidates) {
    if (normalizeDtoolsGroupLabel(candidate) === norm) return candidate.trim();
  }
  return null;
}

function slugId(prefix: string, label: string): string {
  const slug = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${prefix}-${slug || "unnamed"}`;
}

export function collectUniqueSystemNames(lines: DtoolsReviseLine[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of [...lines].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.key.localeCompare(b.key))) {
    const name = String(line.systemName ?? "").trim();
    if (!name) continue;
    const key = normalizeDtoolsGroupLabel(name);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

export function collectRoomNamesForSystem(lines: DtoolsReviseLine[], systemName: string): string[] {
  const systemNorm = normalizeDtoolsGroupLabel(systemName);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of [...lines].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.key.localeCompare(b.key))) {
    if (normalizeDtoolsGroupLabel(line.systemName) !== systemNorm) continue;
    const room = String(line.roomName ?? "").trim();
    if (!room) continue;
    const key = normalizeDtoolsGroupLabel(room);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(room);
  }
  return out;
}

function resolveSystemIds(lines: DtoolsReviseLine[], systemName: string): { systemId: string | null; systemName: string } {
  const trimmed = systemName.trim() || "Unassigned system";
  const existing = findMatchingGroupLabel(collectUniqueSystemNames(lines), trimmed) ?? trimmed;
  const norm = normalizeDtoolsGroupLabel(existing);
  const match = lines.find((line) => normalizeDtoolsGroupLabel(line.systemName) === norm);
  return {
    systemName: existing,
    systemId: match?.systemId ?? slugId("sys", existing),
  };
}

function resolveRoomIds(
  lines: DtoolsReviseLine[],
  systemName: string,
  roomName: string,
): { locationId: string | null; roomName: string | null } {
  const trimmed = roomName.trim();
  if (!trimmed) return { locationId: null, roomName: null };
  const systemNorm = normalizeDtoolsGroupLabel(systemName);
  const rooms = collectRoomNamesForSystem(lines, systemName);
  const existing = findMatchingGroupLabel(rooms, trimmed) ?? trimmed;
  const roomNorm = normalizeDtoolsGroupLabel(existing);
  const match = lines.find(
    (line) =>
      normalizeDtoolsGroupLabel(line.systemName) === systemNorm &&
      normalizeDtoolsGroupLabel(line.roomName) === roomNorm,
  );
  return {
    roomName: existing,
    locationId: match?.locationId ?? slugId("room", `${systemName}-${existing}`),
  };
}

export function assignDtoolsReviseLineSystemRoom(
  lines: DtoolsReviseLine[],
  lineKey: string,
  systemNameInput: string,
  roomNameInput: string,
): DtoolsReviseLine[] {
  const source = lines.find((line) => line.key === lineKey);
  if (!source) return lines;

  const { systemName, systemId } = resolveSystemIds(lines, systemNameInput);
  const { roomName, locationId } = resolveRoomIds(lines, systemName, roomNameInput);
  const oldSystemNorm = normalizeDtoolsGroupLabel(canonicalDtoolsLineSystemName(source));
  const newSystemNorm = normalizeDtoolsGroupLabel(systemName);
  const systemChanged = oldSystemNorm !== newSystemNorm;
  const roomChanged =
    normalizeDtoolsGroupLabel(source.roomName) !== normalizeDtoolsGroupLabel(roomName ?? "");

  return lines.map((line) => {
    if (line.key === lineKey) {
      let next: DtoolsReviseLine = {
        ...line,
        systemName,
        systemId,
        roomName,
        locationId,
      };
      if (systemChanged && (line.parentId || line.isAccessory)) {
        next = { ...next, parentId: null, isAccessory: false };
      }
      return next;
    }
    if (line.parentId === source.id && (systemChanged || roomChanged)) {
      return {
        ...line,
        systemName,
        systemId,
        roomName,
        locationId,
      };
    }
    return line;
  });
}

/** Clear demotion overrides when a line is promoted by cross-system move. */
export function hierarchyOverridesAfterLineSystemRoomChange(
  previousLines: DtoolsReviseLine[],
  nextLines: DtoolsReviseLine[],
  lineKey: string,
  overrides: DtoolsHierarchyOverrides,
): DtoolsHierarchyOverrides {
  const before = previousLines.find((line) => line.key === lineKey);
  const after = nextLines.find((line) => line.key === lineKey);
  if (!before || !after) return overrides;

  const systemChanged =
    normalizeDtoolsGroupLabel(canonicalDtoolsLineSystemName(before)) !==
    normalizeDtoolsGroupLabel(canonicalDtoolsLineSystemName(after));
  if (!systemChanged || (!before.parentId && !before.isAccessory)) return overrides;

  const parentByLineId = { ...overrides.parentByLineId };
  for (const key of [after.id, after.dtoolsItemId].filter(Boolean) as string[]) {
    parentByLineId[key] = null;
  }
  return { parentByLineId };
}

export function updateDtoolsReviseLine(
  lines: DtoolsReviseLine[],
  lineKey: string,
  patch: Partial<
    Pick<
      DtoolsReviseLine,
      | "manufacturer"
      | "model"
      | "category"
      | "polishedDescription"
      | "dtoolsDescription"
      | "quantity"
      | "unitPrice"
      | "baseUnitPrice"
      | "lineTotal"
      | "imageUrl"
      | "systemName"
      | "roomName"
      | "systemId"
      | "locationId"
      | "catalogProductId"
      | "catalogMatchConfidence"
      | "proposalImageOverride"
      | "proposalDescriptionOverride"
      | "displayName"
      | "displayNameManuallyEdited"
      | "pricingMethod"
      | "percentageOfEquipment"
      | "clarityServiceUserOverride"
    >
  >,
): DtoolsReviseLine[] {
  return lines.map((line) => {
    if (line.key !== lineKey) return line;
    const qty = patch.quantity != null ? Math.max(1, Math.round(patch.quantity)) : line.quantity;
    const unitPrice = patch.unitPrice != null ? Math.max(0, patch.unitPrice) : line.unitPrice;
    const qtyChanged = patch.quantity != null;
    const priceChanged = patch.unitPrice != null;
    const lineTotal =
      patch.lineTotal != null
        ? Math.max(0, patch.lineTotal)
        : qtyChanged || priceChanged
          ? roundMoney(unitPrice * qty)
          : Math.max(0, Number(line.lineTotal) || 0);
    const baseUnitPrice =
      patch.baseUnitPrice != null
        ? Math.max(0, patch.baseUnitPrice)
        : priceChanged
          ? unitPrice
          : line.baseUnitPrice ?? unitPrice;
    return {
      ...line,
      ...patch,
      quantity: qty,
      unitPrice,
      baseUnitPrice,
      lineTotal,
    };
  });
}

export function shouldUseDtoolsReviseHierarchy(
  header: ProposalHeaderForDtoolsView,
  items?: ProposalDbItemForDtoolsView[],
): boolean {
  return shouldRenderDtoolsBeautifiedProposalView(header, items);
}

function dtoolsItemIdFromRawText(rawText: string | null | undefined): string {
  const text = String(rawText ?? "").trim();
  if (!text) return "";
  try {
    const parsed = JSON.parse(text) as { source?: string; dtoolsItemId?: string };
    if (parsed.source !== DTOOLS_JSON_SOURCE) return "";
    return String(parsed.dtoolsItemId ?? "").trim();
  } catch {
    return "";
  }
}

export function proposalItemsToDtoolsReviseLines(
  items: ProposalDbItemForDtoolsView[],
  parsedMetadataJson?: unknown,
  header?: ProposalHeaderForDtoolsView,
): DtoolsReviseLine[] {
  const headerForResolve: ProposalHeaderForDtoolsView = header ?? {
    proposalSource: parsedMetadataJson ? "dtools_json" : null,
    parsedMetadataJson,
  };
  const resolvedItems = resolveDtoolsProposalItemsForLiveView(headerForResolve, items);
  const staging = buildDtoolsStagingLinesFromProposalItems(resolvedItems, parsedMetadataJson ?? header?.parsedMetadataJson);
  const dbByDtoolsId = new Map<string, number>();
  for (const item of items) {
    const dtoolsId = dtoolsItemIdFromRawText(item.rawText);
    if (dtoolsId && item.id > 0) dbByDtoolsId.set(dtoolsId, item.id);
  }
  return staging.map((line, idx) => {
    const resolvedId = resolvedItems[idx]?.id;
    return {
      ...line,
      key: `dtools-${line.id}-${idx}`,
      persistedItemId:
        dbByDtoolsId.get(String(line.dtoolsItemId ?? "").trim()) ??
        (resolvedId != null && resolvedId > 0 ? resolvedId : undefined),
    };
  });
}

export function normalizeDtoolsReviseLines(lines: DtoolsReviseLine[]): DtoolsReviseLine[] {
  return sanitizeStagingLineFields(lines);
}

export function elevateReviseServiceLinesToRoomPrimaries(
  lines: DtoolsReviseLine[],
  overrides?: DtoolsHierarchyOverrides | null,
): DtoolsReviseLine[] {
  return elevateServiceLinesToRoomPrimaries(lines, overrides);
}

export function buildDtoolsReviseHierarchy(
  lines: DtoolsReviseLine[],
  overrides?: DtoolsHierarchyOverrides | null,
) {
  const normalized = normalizeDtoolsReviseLines(lines);
  const elevated = elevateReviseServiceLinesToRoomPrimaries(normalized, overrides);
  const applied = applyHierarchyOverridesToLines(elevated, overrides);
  return groupIdenticalProductsInReviewHierarchy(buildReviewHierarchy(applied));
}

export function buildDtoolsReviseHierarchySnapshot(
  lines: DtoolsReviseLine[],
  parentParsedMetadataJson?: unknown,
  overrides?: DtoolsHierarchyOverrides | null,
  virtualRooms: DtoolsReviseVirtualRoom[] = [],
): Record<string, unknown> {
  const parentMeta =
    parentParsedMetadataJson && typeof parentParsedMetadataJson === "object" && !Array.isArray(parentParsedMetadataJson)
      ? (parentParsedMetadataJson as Record<string, unknown>)
      : {};
  const existingSnapshot =
    parentMeta.dtoolsHierarchySnapshot &&
    typeof parentMeta.dtoolsHierarchySnapshot === "object" &&
    !Array.isArray(parentMeta.dtoolsHierarchySnapshot)
      ? (parentMeta.dtoolsHierarchySnapshot as Record<string, unknown>)
      : {};
  const snapshot = buildDtoolsHierarchySnapshotFromLines(lines, {
    sessionId: String(existingSnapshot.sessionId ?? parentMeta.dtoolsStagingSessionId ?? "revise"),
    fileName: typeof existingSnapshot.fileName === "string" ? existingSnapshot.fileName : null,
    proposalRef: typeof existingSnapshot.proposalRef === "string" ? existingSnapshot.proposalRef : null,
  }) as Record<string, unknown>;
  const pruned = pruneVirtualRoomsCoveredByLines(mergeVirtualRoomLists(virtualRooms), lines);
  if (pruned.length) {
    snapshot.virtualRooms = pruned.map((room) => ({
      systemName: room.systemName,
      systemId: room.systemId,
      roomName: room.roomName,
      locationId: room.locationId,
    }));
  } else {
    delete snapshot.virtualRooms;
  }
  if (overrides && Object.keys(overrides.parentByLineId).length > 0) {
    return { ...snapshot, hierarchyOverrides: overrides };
  }
  return snapshot;
}

export function dtoolsReviseLineToCreateItem(line: DtoolsReviseLine): DtoolsReviseCreateItemPayload {
  const confirm = stagingLineToConfirmItem(line);
  const methodRaw = String(line.pricingMethod ?? "").toLowerCase();
  const isClarityService = isClarityStandardServiceProduct({
    brand: line.manufacturer,
    manufacturer: line.manufacturer,
    model: line.model,
    name: line.displayName ?? line.model,
    displayName: line.displayName,
    sku: line.catalogId,
    catalogueKey: line.catalogueKey,
  });
  const pricingMethod =
    methodRaw === "percentage_of_equipment"
      ? "percentage_of_equipment"
      : methodRaw === "fixed" || isClarityService
        ? "fixed"
        : null;
  const percentageOfEquipment =
    pricingMethod === "percentage_of_equipment" && line.percentageOfEquipment != null
      ? Number(line.percentageOfEquipment)
      : null;
  return {
    description: confirm.description,
    unitPrice: confirm.unitPrice,
    qty: confirm.quantity,
    lineTotal: confirm.totalPrice,
    name: confirm.name,
    brand: confirm.brand,
    model: confirm.model,
    category: confirm.category,
    rawText: buildDtoolsStagingLineRawText(line),
    imageUrlSnapshot: confirm.imageUrlSnapshot ?? null,
    displayName: confirm.displayName ?? null,
    displayNameManuallyEdited: line.displayNameManuallyEdited === true,
    pricingMethod,
    percentageOfEquipment:
      percentageOfEquipment != null && Number.isFinite(percentageOfEquipment)
        ? percentageOfEquipment
        : null,
    cleoDescription: confirm.cleoDescription ?? null,
    customerExplanation: String(line.customerExplanation ?? "").trim() || null,
  };
}

export function dtoolsReviseLinesToCreateItems(lines: DtoolsReviseLine[]): DtoolsReviseCreateItemPayload[] {
  return sanitizeStagingLineFields(lines)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((line, idx) => {
      const item = dtoolsReviseLineToCreateItem({ ...line, sortOrder: idx });
      return item;
    });
}

export function displayTitleForDtoolsReviseLine(line: DtoolsReviseLine): string {
  return resolveProductDisplayName({
    brand: line.manufacturer,
    model: line.model,
    displayName: line.displayName,
    description: line.polishedDescription || line.dtoolsDescription,
  });
}

export function displaySubtitleForDtoolsReviseLine(line: DtoolsReviseLine): string | null {
  const technical = formatProductBrandModelReference({
    brand: line.manufacturer,
    model: line.model,
  });
  const title = displayTitleForDtoolsReviseLine(line);
  return technical && technical !== title ? technical : null;
}

export function isDtoolsJsonProposalHeader(header: { proposalSource?: string | null; parsedMetadataJson?: unknown }): boolean {
  return isDtoolsJsonProposalSourceValue(header.proposalSource) && hasDtoolsHierarchySnapshot(header.parsedMetadataJson);
}

export type DtoolsReviseRoomRef = {
  systemName: string;
  systemId: string | null;
  roomName: string;
  locationId: string | null;
};

export type DtoolsReviseVirtualRoom = DtoolsReviseRoomRef;

export type DtoolsReviseProductPick = {
  brand: string | null;
  model: string | null;
  displayName: string;
  description: string;
  unitPrice: number;
  imageUrl?: string | null;
  catalogueKey?: string;
  projectCatalogueId?: number | null;
  /** Retail / Portal `catalogue_items.id` — becomes catalogProductId on the revise line. */
  retailCatalogueItemId?: number | null;
  catalogProductId?: number | null;
  catalogMatchConfidence?: "matched" | "possible" | "none" | null;
  sku?: string | null;
  category?: string | null;
  pricingMethod?: "fixed" | "percentage_of_equipment" | null;
  percentageOfEquipment?: number | null;
};

function lineMatchesRoom(
  line: DtoolsReviseLine,
  systemName: string,
  roomName: string,
): boolean {
  return (
    normalizeDtoolsGroupLabel(canonicalDtoolsLineSystemName(line)) === normalizeDtoolsGroupLabel(systemName) &&
    normalizeDtoolsGroupLabel(line.roomName) === normalizeDtoolsGroupLabel(roomName)
  );
}

function lineMatchesSystem(line: DtoolsReviseLine, systemName: string): boolean {
  return (
    normalizeDtoolsGroupLabel(canonicalDtoolsLineSystemName(line)) === normalizeDtoolsGroupLabel(systemName)
  );
}

export function sumReviseLineTotals(lines: DtoolsReviseLine[]): number {
  return roundMoney(
    lines.reduce((sum, line) => sum + Math.max(0, Number(line.lineTotal) || 0), 0),
  );
}

export function countLinesInRoom(lines: DtoolsReviseLine[], systemName: string, roomName: string): number {
  return lines.filter((line) => lineMatchesRoom(line, systemName, roomName)).length;
}

export function sumLinesInRoom(lines: DtoolsReviseLine[], systemName: string, roomName: string): number {
  return sumReviseLineTotals(lines.filter((line) => lineMatchesRoom(line, systemName, roomName)));
}

export function countRoomsInSystem(lines: DtoolsReviseLine[], systemName: string, virtualRooms: DtoolsReviseVirtualRoom[] = []): number {
  const names = new Set<string>();
  for (const line of lines) {
    if (!lineMatchesSystem(line, systemName)) continue;
    const room = String(line.roomName ?? "").trim();
    if (room) names.add(normalizeDtoolsGroupLabel(room));
  }
  for (const room of virtualRooms) {
    if (normalizeDtoolsGroupLabel(room.systemName) !== normalizeDtoolsGroupLabel(systemName)) continue;
    names.add(normalizeDtoolsGroupLabel(room.roomName));
  }
  return names.size;
}

export function sumLinesInSystem(lines: DtoolsReviseLine[], systemName: string): number {
  return sumReviseLineTotals(lines.filter((line) => lineMatchesSystem(line, systemName)));
}

export function createNewReviseLineId(): string {
  return `revise-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function nextReviseSortOrder(lines: DtoolsReviseLine[]): number {
  return lines.reduce((max, line) => Math.max(max, line.sortOrder ?? 0), -1) + 1;
}

export function renameRoomInLines(
  lines: DtoolsReviseLine[],
  systemName: string,
  oldRoomName: string,
  newRoomName: string,
): DtoolsReviseLine[] {
  const trimmed = newRoomName.trim();
  if (!trimmed) return lines;
  const { roomName, locationId } = resolveRoomIds(lines, systemName, trimmed);
  return lines.map((line) => {
    if (!lineMatchesRoom(line, systemName, oldRoomName)) return line;
    return { ...line, roomName, locationId };
  });
}

export function renameSystemInLines(
  lines: DtoolsReviseLine[],
  oldSystemName: string,
  newSystemName: string,
): DtoolsReviseLine[] {
  const trimmed = newSystemName.trim();
  if (!trimmed) return lines;
  const { systemName, systemId } = resolveSystemIds(lines, trimmed);
  const oldNorm = normalizeDtoolsGroupLabel(oldSystemName);
  return lines.map((line) => {
    if (normalizeDtoolsGroupLabel(line.systemName) !== oldNorm) return line;
    return { ...line, systemName, systemId };
  });
}

export function deleteRoomFromLines(
  lines: DtoolsReviseLine[],
  systemName: string,
  roomName: string,
): DtoolsReviseLine[] {
  return lines.filter((line) => !lineMatchesRoom(line, systemName, roomName));
}

export function deleteSystemFromLines(lines: DtoolsReviseLine[], systemName: string): DtoolsReviseLine[] {
  return lines.filter((line) => !lineMatchesSystem(line, systemName));
}

export function moveRoomToSystem(
  lines: DtoolsReviseLine[],
  systemName: string,
  roomName: string,
  targetSystemName: string,
): DtoolsReviseLine[] {
  const { systemName: resolvedSystem, systemId } = resolveSystemIds(lines, targetSystemName);
  const { roomName: resolvedRoom, locationId } = resolveRoomIds(lines, resolvedSystem, roomName);
  return lines.map((line) => {
    if (!lineMatchesRoom(line, systemName, roomName)) return line;
    return { ...line, systemName: resolvedSystem, systemId, roomName: resolvedRoom, locationId };
  });
}

export function moveLineToRoom(
  lines: DtoolsReviseLine[],
  lineKey: string,
  target: DtoolsReviseRoomRef,
  options?: { copy?: boolean },
): DtoolsReviseLine[] {
  const source = lines.find((line) => line.key === lineKey);
  if (!source) return lines;
  const { systemName, systemId } = resolveSystemIds(lines, target.systemName);
  const { roomName, locationId } = resolveRoomIds(lines, systemName, target.roomName);
  if (options?.copy) {
    const copy: DtoolsReviseLine = {
      ...source,
      key: createNewReviseLineId(),
      id: createNewReviseLineId(),
      dtoolsItemId: createNewReviseLineId(),
      systemName,
      systemId,
      roomName,
      locationId,
      sortOrder: nextReviseSortOrder(lines),
      parentId: null,
      isAccessory: false,
    };
    return [...lines, copy];
  }
  return assignDtoolsReviseLineSystemRoom(lines, lineKey, systemName, roomName ?? "");
}

export function moveLineToRoomWithHierarchy(
  lines: DtoolsReviseLine[],
  lineKey: string,
  target: DtoolsReviseRoomRef,
  overrides: DtoolsHierarchyOverrides,
  options?: { copy?: boolean },
): { lines: DtoolsReviseLine[]; overrides: DtoolsHierarchyOverrides } {
  const nextLines = moveLineToRoom(lines, lineKey, target, options);
  if (options?.copy) {
    return { lines: nextLines, overrides };
  }
  const nextOverrides = hierarchyOverridesAfterLineSystemRoomChange(lines, nextLines, lineKey, overrides);
  return { lines: nextLines, overrides: nextOverrides };
}

export type DtoolsCloneRoomResult = {
  lines: DtoolsReviseLine[];
  clonedCount: number;
  /** Source lines that share catalogueKey/catalogProductId with an existing target-room line. */
  duplicateMatchCount: number;
};

function lineCatalogueIdentityKey(line: DtoolsReviseLine): string | null {
  const productId = Number(line.catalogProductId);
  if (Number.isFinite(productId) && productId > 0) return `id:${productId}`;
  const catalogueKey = String(line.catalogueKey ?? "").trim();
  if (catalogueKey) return `key:${catalogueKey}`;
  return null;
}

/** Count source lines that already have a catalogue identity present in the target room. */
export function countRoomCatalogueDuplicates(
  targetRoomLines: DtoolsReviseLine[],
  sourceRoomLines: DtoolsReviseLine[],
): number {
  const targetKeys = new Set(
    targetRoomLines.map(lineCatalogueIdentityKey).filter((k): k is string => Boolean(k)),
  );
  if (targetKeys.size === 0) return 0;
  let count = 0;
  for (const line of sourceRoomLines) {
    const key = lineCatalogueIdentityKey(line);
    if (key && targetKeys.has(key)) count += 1;
  }
  return count;
}

export function linesInRoom(
  lines: DtoolsReviseLine[],
  systemName: string,
  roomName: string,
): DtoolsReviseLine[] {
  return lines
    .filter((line) => lineMatchesRoom(line, systemName, roomName))
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.key.localeCompare(b.key));
}

/**
 * Copy every line from source room into target room with new line identities.
 * Preserves catalogue linkage, pricing (incl. lineTotal), and parent/accessory hierarchy.
 * Does not mutate source-room lines.
 */
export function cloneRoomContentsToTarget(
  lines: DtoolsReviseLine[],
  source: DtoolsReviseRoomRef,
  target: DtoolsReviseRoomRef,
): DtoolsCloneRoomResult {
  if (roomRefKey(source) === roomRefKey(target)) {
    return { lines, clonedCount: 0, duplicateMatchCount: 0 };
  }

  const sourceLines = linesInRoom(lines, source.systemName, source.roomName);
  if (sourceLines.length === 0) {
    return { lines, clonedCount: 0, duplicateMatchCount: 0 };
  }

  const targetExisting = linesInRoom(lines, target.systemName, target.roomName);
  const duplicateMatchCount = countRoomCatalogueDuplicates(targetExisting, sourceLines);

  const { systemName, systemId } = resolveSystemIds(lines, target.systemName);
  const { roomName, locationId } = resolveRoomIds(lines, systemName, target.roomName);

  const idMap = new Map<string, string>();
  for (const line of sourceLines) {
    const newId = createNewReviseLineId();
    idMap.set(line.id, newId);
    const dtoolsId = String(line.dtoolsItemId ?? "").trim();
    if (dtoolsId) idMap.set(dtoolsId, newId);
  }

  const sortBase = nextReviseSortOrder(lines);
  const clones: DtoolsReviseLine[] = sourceLines.map((line, idx) => {
    const newId = idMap.get(line.id)!;
    const rawParent = String(line.parentId ?? "").trim();
    const mappedParent = rawParent ? idMap.get(rawParent) ?? null : null;
    return {
      ...line,
      key: createNewReviseLineId(),
      id: newId,
      dtoolsItemId: newId,
      parentId: mappedParent,
      isAccessory: Boolean(mappedParent),
      systemName,
      systemId,
      roomName,
      locationId,
      sortOrder: sortBase + idx,
      persistedItemId: undefined,
    };
  });

  return {
    lines: [...lines, ...clones],
    clonedCount: clones.length,
    duplicateMatchCount,
  };
}

function reviseLineSiblingScope(line: DtoolsReviseLine, lines: DtoolsReviseLine[]): DtoolsReviseLine[] {
  return lines
    .filter(
      (row) =>
        row.systemName === line.systemName &&
        row.roomName === line.roomName &&
        (row.parentId ?? null) === (line.parentId ?? null) &&
        Boolean(row.isAccessory) === Boolean(line.isAccessory),
    )
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.key.localeCompare(b.key));
}

/** Reorder a line within its room scope (same system, room, parent, accessory flag). */
export function moveReviseLineInRoom(
  lines: DtoolsReviseLine[],
  lineKey: string,
  direction: -1 | 1,
): DtoolsReviseLine[] {
  const line = lines.find((row) => row.key === lineKey);
  if (!line) return lines;
  const siblings = reviseLineSiblingScope(line, lines);
  const index = siblings.findIndex((row) => row.key === lineKey);
  const targetIndex = index + direction;
  if (index < 0 || targetIndex < 0 || targetIndex >= siblings.length) return lines;
  const current = siblings[index]!;
  const swap = siblings[targetIndex]!;
  const currentOrder = current.sortOrder ?? index;
  const swapOrder = swap.sortOrder ?? targetIndex;
  return lines.map((row) => {
    if (row.key === current.key) return { ...row, sortOrder: swapOrder };
    if (row.key === swap.key) return { ...row, sortOrder: currentOrder };
    return row;
  });
}

export function canMoveReviseLineInRoom(
  lines: DtoolsReviseLine[],
  lineKey: string,
  direction: -1 | 1,
): boolean {
  const line = lines.find((row) => row.key === lineKey);
  if (!line) return false;
  const siblings = reviseLineSiblingScope(line, lines);
  const index = siblings.findIndex((row) => row.key === lineKey);
  const targetIndex = index + direction;
  return index >= 0 && targetIndex >= 0 && targetIndex < siblings.length;
}

export function createReviseLineForRoom(
  target: DtoolsReviseRoomRef,
  lines: DtoolsReviseLine[],
  partial: Partial<DtoolsReviseLine> & Pick<DtoolsReviseLine, "bucket" | "polishedDescription" | "dtoolsDescription">,
): DtoolsReviseLine {
  const { systemName, systemId } = resolveSystemIds(lines, target.systemName);
  const { roomName, locationId } = resolveRoomIds(lines, systemName, target.roomName);
  const id = createNewReviseLineId();
  const qty = Math.max(1, partial.quantity ?? 1);
  const unitPrice = Math.max(0, partial.unitPrice ?? 0);
  return {
    id,
    dtoolsItemId: id,
    manufacturer: partial.manufacturer ?? null,
    model: partial.model ?? null,
    displayName: partial.displayName ?? null,
    displayNameManuallyEdited: partial.displayNameManuallyEdited === true,
    category: partial.category ?? null,
    subcategory: null,
    dtoolsDescription: partial.dtoolsDescription,
    polishedDescription: partial.polishedDescription,
    quantity: qty,
    baseUnitPrice:
      partial.baseUnitPrice != null && Number.isFinite(Number(partial.baseUnitPrice))
        ? Math.max(0, Number(partial.baseUnitPrice))
        : unitPrice,
    unitPrice,
    lineTotal: unitPrice * qty,
    systemId,
    systemName,
    locationId,
    roomName,
    parentId: null,
    sortOrder: nextReviseSortOrder(lines),
    bucket: partial.bucket,
    imageUrl: partial.imageUrl ?? null,
    projectCatalogueId: partial.projectCatalogueId ?? null,
    catalogueKey: partial.catalogueKey ?? "",
    catalogId: partial.catalogId ?? null,
    componentId: partial.componentId ?? null,
    isAccessory: false,
    mergedSources: partial.mergedSources ?? [],
    catalogProductId: partial.catalogProductId ?? null,
    catalogMatchConfidence: partial.catalogMatchConfidence ?? null,
    proposalImageOverride: partial.proposalImageOverride ?? null,
    proposalDescriptionOverride: partial.proposalDescriptionOverride ?? null,
    pricingMethod: partial.pricingMethod ?? null,
    percentageOfEquipment: partial.percentageOfEquipment ?? null,
    key: createNewReviseLineId(),
  };
}

/**
 * Insert a product from Project / Retail (Portal) Catalogue into a revise room.
 * Catalogue picks must carry a valid match linkage so the line never appears unmatched.
 */
export function createReviseProductLine(
  target: DtoolsReviseRoomRef,
  lines: DtoolsReviseLine[],
  product: DtoolsReviseProductPick,
): DtoolsReviseLine {
  const description = product.description.trim() || product.displayName.trim() || "Product";
  const catalogProductId =
    product.catalogProductId != null && product.catalogProductId > 0
      ? product.catalogProductId
      : product.retailCatalogueItemId != null && product.retailCatalogueItemId > 0
        ? product.retailCatalogueItemId
        : null;
  const fromCatalogue =
    catalogProductId != null ||
    (product.projectCatalogueId != null && product.projectCatalogueId > 0) ||
    product.catalogMatchConfidence === "matched";
  const catalogueKey =
    product.catalogueKey?.trim() ||
    buildDtoolsProjectCatalogKey({
      manufacturer: product.brand,
      model: product.model,
      catalogId: product.sku ?? (catalogProductId != null ? String(catalogProductId) : null),
      displayName: product.displayName,
    });
  const isClarityService = isClarityStandardServiceProduct({
    brand: product.brand,
    manufacturer: product.brand,
    model: product.model,
    name: product.displayName,
    displayName: product.displayName,
    sku: product.sku,
    catalogueKey: product.catalogueKey,
  });
  const pricingMethod =
    product.pricingMethod === "percentage_of_equipment"
      ? "percentage_of_equipment"
      : product.pricingMethod === "fixed" || isClarityService
        ? "fixed"
        : null;
  return createReviseLineForRoom(target, lines, {
    bucket: isClarityService ? "service" : "commercial",
    manufacturer: product.brand,
    model: product.model,
    displayName: product.displayName.trim() || null,
    category: product.category ?? null,
    dtoolsDescription: description,
    polishedDescription: description,
    baseUnitPrice: product.unitPrice,
    unitPrice: product.unitPrice,
    quantity: 1,
    imageUrl: product.imageUrl ?? null,
    projectCatalogueId: product.projectCatalogueId ?? null,
    catalogueKey,
    catalogId: product.sku ?? null,
    catalogProductId,
    catalogMatchConfidence: fromCatalogue
      ? product.catalogMatchConfidence === "possible"
        ? "possible"
        : "matched"
      : product.catalogMatchConfidence ?? null,
    pricingMethod,
    percentageOfEquipment:
      pricingMethod === "percentage_of_equipment" ? product.percentageOfEquipment ?? null : null,
    clarityServiceUserOverride: isClarityService ? true : undefined,
  });
}

/**
 * Bulk-add helper for multi-select catalogue inserts.
 * Each pick goes through {@link createReviseProductLine} so catalogue linkage cannot be skipped.
 */
export function createReviseProductLinesFromCataloguePicks(
  target: DtoolsReviseRoomRef,
  lines: DtoolsReviseLine[],
  products: DtoolsReviseProductPick[],
): DtoolsReviseLine[] {
  let next = lines;
  const created: DtoolsReviseLine[] = [];
  for (const product of products) {
    const line = createReviseProductLine(target, next, product);
    created.push(line);
    next = appendReviseLine(next, line);
  }
  return created;
}

export type SupportingComponentQuantityPick = {
  product: DtoolsReviseProductPick;
  quantity: number;
};

export type AttachSupportingComponentsResult = {
  lines: DtoolsReviseLine[];
  added: DtoolsReviseLine[];
  quantityIncreased: Array<{
    lineKey: string;
    previousQuantity: number;
    nextQuantity: number;
    displayName: string;
  }>;
  parentKey: string;
  parentId: string;
  /** Parent commercial lineTotal is never mutated by this helper. */
  parentLineTotal: number;
};

/** Catalogue identity used to detect accidental duplicate supporting lines under one parent. */
export function productPickIdentityKey(product: DtoolsReviseProductPick): string | null {
  const productId = Number(product.catalogProductId ?? product.retailCatalogueItemId);
  if (Number.isFinite(productId) && productId > 0) return `id:${productId}`;
  const projectId = Number(product.projectCatalogueId);
  if (Number.isFinite(projectId) && projectId > 0) return `project:${projectId}`;
  const catalogueKey = String(product.catalogueKey ?? "").trim();
  if (catalogueKey) return `key:${catalogueKey}`;
  const brand = String(product.brand ?? "").trim().toLowerCase();
  const model = String(product.model ?? "").trim().toLowerCase();
  if (brand || model) return `bm:${brand}|${model}`;
  return null;
}

export function findSupportingDuplicateUnderParent(
  lines: DtoolsReviseLine[],
  parentId: string,
  product: DtoolsReviseProductPick,
): DtoolsReviseLine | null {
  const identity = productPickIdentityKey(product);
  if (!identity) return null;
  const parentNorm = String(parentId);
  for (const line of lines) {
    if (String(line.parentId ?? "") !== parentNorm) continue;
    if (!line.isAccessory && !line.parentId) continue;
    const existing = lineCatalogueIdentityKey(line);
    if (existing && existing === identity) return line;
    // Also match projectCatalogueId when identity used project: prefix
    if (identity.startsWith("project:")) {
      const projectId = Number(product.projectCatalogueId);
      if (Number(line.projectCatalogueId) === projectId) return line;
    }
  }
  return null;
}

/**
 * Attach one or more catalogue products as supporting components under a primary item.
 * Inherits System/Room from the parent. Appends beneath existing accessories.
 * Duplicate catalogue products under the same parent increase quantity instead of creating a new line.
 * Does not change the parent's unitPrice or lineTotal.
 */
export function attachSupportingComponentsToParent(
  lines: DtoolsReviseLine[],
  parentKey: string,
  picks: SupportingComponentQuantityPick[],
  overrides: DtoolsHierarchyOverrides = { parentByLineId: {} },
): { result: AttachSupportingComponentsResult; overrides: DtoolsHierarchyOverrides } {
  const parent = lines.find((row) => row.key === parentKey);
  if (!parent) {
    return {
      result: {
        lines,
        added: [],
        quantityIncreased: [],
        parentKey,
        parentId: "",
        parentLineTotal: 0,
      },
      overrides,
    };
  }

  const parentLineTotal = Number(parent.lineTotal) || 0;
  const roomRef: DtoolsReviseRoomRef = {
    systemName: parent.systemName ?? "System",
    systemId: parent.systemId,
    roomName: parent.roomName ?? "Room",
    locationId: parent.locationId,
  };

  let nextLines = [...lines];
  let nextOverrides: DtoolsHierarchyOverrides = {
    parentByLineId: { ...overrides.parentByLineId },
  };
  const added: DtoolsReviseLine[] = [];
  const quantityIncreased: AttachSupportingComponentsResult["quantityIncreased"] = [];

  for (const pick of picks) {
    const qty = Math.max(1, Math.round(Number(pick.quantity) || 1));
    const duplicate = findSupportingDuplicateUnderParent(nextLines, parent.id, pick.product);
    if (duplicate) {
      const previousQuantity = Math.max(1, Number(duplicate.quantity) || 1);
      const nextQuantity = previousQuantity + qty;
      const unitPrice = Math.max(0, Number(duplicate.unitPrice) || 0);
      nextLines = nextLines.map((row) =>
        row.key === duplicate.key
          ? {
              ...row,
              quantity: nextQuantity,
              lineTotal: unitPrice * nextQuantity,
            }
          : row,
      );
      quantityIncreased.push({
        lineKey: duplicate.key,
        previousQuantity,
        nextQuantity,
        displayName: displayTitleForDtoolsReviseLine(duplicate),
      });
      continue;
    }

    const created = createReviseProductLine(roomRef, nextLines, pick.product);
    const accessory: DtoolsReviseLine = {
      ...created,
      quantity: qty,
      lineTotal: Math.max(0, created.unitPrice) * qty,
      parentId: parent.id,
      isAccessory: true,
      systemName: parent.systemName,
      systemId: parent.systemId,
      roomName: parent.roomName,
      locationId: parent.locationId,
      sortOrder: nextReviseSortOrder(nextLines),
    };
    nextLines = appendReviseLine(nextLines, accessory);
    nextOverrides = {
      parentByLineId: {
        ...nextOverrides.parentByLineId,
        [accessory.id]: parent.id,
      },
    };
    added.push(accessory);
  }

  // Ensure parent commercial totals are untouched.
  nextLines = nextLines.map((row) =>
    row.key === parent.key
      ? { ...row, lineTotal: parentLineTotal, unitPrice: parent.unitPrice, quantity: parent.quantity }
      : row,
  );

  return {
    result: {
      lines: nextLines,
      added,
      quantityIncreased,
      parentKey: parent.key,
      parentId: parent.id,
      parentLineTotal,
    },
    overrides: nextOverrides,
  };
}

export function createReviseLabourLine(target: DtoolsReviseRoomRef, lines: DtoolsReviseLine[]): DtoolsReviseLine {
  return createReviseLineForRoom(target, lines, {
    bucket: "service",
    manufacturer: null,
    model: "Labour",
    dtoolsDescription: "Installation labour",
    polishedDescription: "Installation labour",
    unitPrice: 0,
    quantity: 1,
  });
}

export function createReviseCableLine(target: DtoolsReviseRoomRef, lines: DtoolsReviseLine[]): DtoolsReviseLine {
  return createReviseLineForRoom(target, lines, {
    bucket: "cable",
    manufacturer: null,
    model: "Cable",
    dtoolsDescription: "Cable run",
    polishedDescription: "Cable run",
    unitPrice: 0,
    quantity: 1,
  });
}

export function appendReviseLine(lines: DtoolsReviseLine[], line: DtoolsReviseLine): DtoolsReviseLine[] {
  return [...lines, line];
}

export function createVirtualRoom(
  lines: DtoolsReviseLine[],
  systemName: string,
  roomName: string,
): DtoolsReviseVirtualRoom {
  const { systemName: resolvedSystem, systemId } = resolveSystemIds(lines, systemName);
  const { roomName: resolvedRoom, locationId } = resolveRoomIds(lines, resolvedSystem, roomName);
  return {
    systemName: resolvedSystem,
    systemId,
    roomName: resolvedRoom ?? roomName.trim(),
    locationId,
  };
}

/** True when a room name already exists under the same system (case-insensitive, trimmed). */
export function roomNameExistsInSystem(
  lines: DtoolsReviseLine[],
  systemName: string,
  roomName: string,
  virtualRooms: DtoolsReviseVirtualRoom[] = [],
): boolean {
  const trimmed = String(roomName ?? "").trim();
  if (!trimmed) return false;
  const roomNorm = normalizeDtoolsGroupLabel(trimmed);
  const systemNorm = normalizeDtoolsGroupLabel(systemName);
  for (const name of collectRoomNamesForSystem(lines, systemName)) {
    if (normalizeDtoolsGroupLabel(name) === roomNorm) return true;
  }
  for (const room of virtualRooms) {
    if (normalizeDtoolsGroupLabel(room.systemName) !== systemNorm) continue;
    if (normalizeDtoolsGroupLabel(room.roomName) === roomNorm) return true;
  }
  return false;
}

/** Drop virtual rooms that already have at least one line in the workspace. */
export function pruneVirtualRoomsCoveredByLines(
  virtualRooms: DtoolsReviseVirtualRoom[],
  lines: DtoolsReviseLine[],
): DtoolsReviseVirtualRoom[] {
  return virtualRooms.filter(
    (room) => countLinesInRoom(lines, room.systemName, room.roomName) === 0,
  );
}

export function mergeVirtualRoomLists(
  ...lists: Array<DtoolsReviseVirtualRoom[] | null | undefined>
): DtoolsReviseVirtualRoom[] {
  const out: DtoolsReviseVirtualRoom[] = [];
  const seen = new Set<string>();
  for (const list of lists) {
    for (const room of list ?? []) {
      const key = roomRefKey(room);
      if (!key.includes("::") || seen.has(key)) continue;
      const name = String(room.roomName ?? "").trim();
      const system = String(room.systemName ?? "").trim();
      if (!name || !system) continue;
      seen.add(key);
      out.push({
        systemName: system,
        systemId: room.systemId ?? null,
        roomName: name,
        locationId: room.locationId ?? null,
      });
    }
  }
  return out;
}

export function readDtoolsVirtualRooms(parsedMetadataJson: unknown): DtoolsReviseVirtualRoom[] {
  if (!parsedMetadataJson || typeof parsedMetadataJson !== "object" || Array.isArray(parsedMetadataJson)) {
    return [];
  }
  const meta = parsedMetadataJson as {
    dtoolsVirtualRooms?: unknown;
    dtoolsHierarchySnapshot?: { virtualRooms?: unknown };
  };
  const fromRoot = Array.isArray(meta.dtoolsVirtualRooms) ? meta.dtoolsVirtualRooms : [];
  const fromSnap = Array.isArray(meta.dtoolsHierarchySnapshot?.virtualRooms)
    ? meta.dtoolsHierarchySnapshot!.virtualRooms
    : [];
  const parseList = (raw: unknown[]): DtoolsReviseVirtualRoom[] =>
    raw
      .map((entry) => {
        if (!entry || typeof entry !== "object" || Array.isArray(entry)) return null;
        const row = entry as Record<string, unknown>;
        const systemName = String(row.systemName ?? "").trim();
        const roomName = String(row.roomName ?? "").trim();
        if (!systemName || !roomName) return null;
        return {
          systemName,
          systemId: row.systemId != null ? String(row.systemId) : null,
          roomName,
          locationId: row.locationId != null ? String(row.locationId) : null,
        } satisfies DtoolsReviseVirtualRoom;
      })
      .filter(Boolean) as DtoolsReviseVirtualRoom[];

  return mergeVirtualRoomLists(parseList(fromRoot), parseList(fromSnap as unknown[]));
}

export function withDtoolsVirtualRooms(
  parsedMetadataJson: unknown,
  virtualRooms: DtoolsReviseVirtualRoom[],
): Record<string, unknown> {
  const base =
    parsedMetadataJson && typeof parsedMetadataJson === "object" && !Array.isArray(parsedMetadataJson)
      ? { ...(parsedMetadataJson as Record<string, unknown>) }
      : {};
  const pruned = mergeVirtualRoomLists(virtualRooms);
  if (!pruned.length) {
    delete base.dtoolsVirtualRooms;
    return base;
  }
  base.dtoolsVirtualRooms = pruned.map((room) => ({
    systemName: room.systemName,
    systemId: room.systemId,
    roomName: room.roomName,
    locationId: room.locationId,
  }));
  return base;
}

export function listRoomGroups(
  lines: DtoolsReviseLine[],
  virtualRooms: DtoolsReviseVirtualRoom[] = [],
): DtoolsReviseRoomRef[] {
  const seen = new Set<string>();
  const groups: DtoolsReviseRoomRef[] = [];
  const add = (ref: DtoolsReviseRoomRef) => {
    const key = `${normalizeDtoolsGroupLabel(ref.systemName)}::${normalizeDtoolsGroupLabel(ref.roomName)}`;
    if (seen.has(key)) return;
    seen.add(key);
    groups.push(ref);
  };
  for (const line of [...lines].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.key.localeCompare(b.key),
  )) {
    const systemName = String(line.systemName ?? "").trim();
    const roomName = String(line.roomName ?? "").trim();
    if (!systemName || !roomName) continue;
    add({
      systemName,
      systemId: line.systemId,
      roomName,
      locationId: line.locationId,
    });
  }
  for (const room of virtualRooms) {
    add(room);
  }
  // First-appearance order by sortOrder — never alphabetical.
  return groups;
}

export function roomRefKey(ref: Pick<DtoolsReviseRoomRef, "systemName" | "roomName">): string {
  return `${normalizeDtoolsGroupLabel(ref.systemName)}::${normalizeDtoolsGroupLabel(ref.roomName)}`;
}

export function systemRefKey(systemName: string): string {
  return normalizeDtoolsGroupLabel(systemName);
}
